@file:Suppress("MagicNumber", "TooGenericExceptionCaught")

package com.nonopn.skydome.common.app.pages.promocodes

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skydome.common.app.api.BackendErrorException
import com.nonopn.skydome.common.app.api.impl.AccountStatus
import com.nonopn.skydome.common.app.api.impl.ApiNormal
import com.nonopn.skydome.common.app.api.impl.ProbeApp
import com.nonopn.skydome.common.app.api.impl.SubscriptionPublicState
import com.nonopn.skydome.common.app.home.AppModel
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.promo_benefit_free
import com.nonopn.skywatcher.common.app.res.promo_benefit_months
import com.nonopn.skywatcher.common.app.res.promo_benefit_months_percent
import com.nonopn.skywatcher.common.app.res.promo_benefit_none
import com.nonopn.skywatcher.common.app.res.promo_benefit_percent
import com.nonopn.skywatcher.common.app.res.promo_err_already_used
import com.nonopn.skywatcher.common.app.res.promo_err_expired_user
import com.nonopn.skywatcher.common.app.res.promo_err_not_applicable
import com.nonopn.skywatcher.common.app.res.promo_err_not_found_user
import com.nonopn.skywatcher.common.app.res.promo_err_unavailable
import com.nonopn.skywatcher.common.app.res.promo_field_applied
import com.nonopn.skywatcher.common.app.res.promo_field_apply
import com.nonopn.skywatcher.common.app.res.promo_field_checking
import com.nonopn.skywatcher.common.app.res.promo_field_label
import com.nonopn.skywatcher.common.app.res.promo_field_placeholder
import com.nonopn.skywatcher.common.app.res.promo_field_remove
import com.nonopn.skywatcher.design.theme.AppColor
import eu.codlab.compose.widgets.TextNormal
import kotlinx.coroutines.CancellationException
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

private val PromoError = Color(0xFFE63B2E)
private val PromoSuccess = Color(0xFF2E9E5B)

/** A code accepted by GET /api/checkout/promotion-codes/{code}: sent as `promotionCode` of the checkout. */
data class AppliedPromoCode(val code: String, val percentOff: Int?, val freeMonths: Int?) {
    val benefit get() = promoBenefit(percentOff, freeMonths)
}

/** State of the "Promo code" field shown before the payment. */
data class PromoCodeUiState(
    val input: String = "",
    val checking: Boolean = false,
    val applied: AppliedPromoCode? = null,
    val error: PromoCheckFailure? = null,
) {
    /** The code to send with the checkout: only a code accepted by the check. */
    val codeToSend: String? get() = applied?.code

    fun typing(text: String) = copy(input = normalizePromoCode(text), error = null)
    fun startChecking() = copy(checking = true, error = null, applied = null)
    fun accepted(code: AppliedPromoCode) = copy(checking = false, applied = code, error = null, input = code.code)
    fun refused(failure: PromoCheckFailure) = copy(checking = false, applied = null, error = failure)
    fun removed() = PromoCodeUiState()
}

sealed interface PromoCheckResult {
    data class Accepted(val code: AppliedPromoCode) : PromoCheckResult
    data class Refused(val failure: PromoCheckFailure) : PromoCheckResult
}

/**
 * Checks a code with the bearer of the logged in account (the token of POST /register works right away too).
 * Never throws, cancellation excepted.
 */
suspend fun AppModel.checkPromoCode(input: String, app: String = ProbeApp.STRIKE_RADAR): PromoCheckResult {
    val code = normalizePromoCode(input)
    if (code.isEmpty()) return PromoCheckResult.Refused(PromoCheckFailure.NotFound)
    val api = states.value.apiProvider?.apiNormal as? ApiNormal
        ?: return PromoCheckResult.Refused(PromoCheckFailure.Unavailable)
    return try {
        val answer = api.checkPromotionCode(code, app)
        PromoCheckResult.Accepted(AppliedPromoCode(answer.code.ifBlank { code }, answer.percentOff, answer.freeMonths))
    } catch (e: CancellationException) {
        throw e
    } catch (e: Throwable) {
        PromoCheckResult.Refused(e.toPromoCheckFailure())
    }
}

/** Failure of the check route for any exception (network = Unavailable). */
fun Throwable.toPromoCheckFailure(): PromoCheckFailure =
    if (this is BackendErrorException) classifyPromoCheckFailure(code.value, body) else PromoCheckFailure.Unavailable

/**
 * Promotion code failure of a checkout (/probes, /account, /upgrade re-check the code), or null when the error
 * is not about the code (handled by the usual probe dialog).
 */
fun Throwable.promoCheckoutFailure(): PromoCheckFailure? =
    (this as? BackendErrorException)?.let { promoFailureForErrorCode(promoErrorCodeOf(it.body)) }

fun promoCheckFailureMessage(failure: PromoCheckFailure): StringResource = when (failure) {
    PromoCheckFailure.NotFound -> Res.string.promo_err_not_found_user
    PromoCheckFailure.Expired -> Res.string.promo_err_expired_user
    PromoCheckFailure.AlreadyUsed -> Res.string.promo_err_already_used
    PromoCheckFailure.NotApplicable -> Res.string.promo_err_not_applicable
    PromoCheckFailure.Unavailable -> Res.string.promo_err_unavailable
}

/** "−20 % on every invoice", "3 free months", "2 free months then −10 %...", "Free (−100 %)". */
@Composable
fun promoBenefitLabel(benefit: PromoBenefit): String = when (benefit) {
    is PromoBenefit.Percent -> stringResource(Res.string.promo_benefit_percent, benefit.percent)
    is PromoBenefit.FreeMonths -> stringResource(Res.string.promo_benefit_months, benefit.months)
    is PromoBenefit.FreeMonthsThenPercent ->
        stringResource(Res.string.promo_benefit_months_percent, benefit.months, benefit.percent)
    PromoBenefit.Free -> stringResource(Res.string.promo_benefit_free)
    PromoBenefit.None -> stringResource(Res.string.promo_benefit_none)
}

/**
 * "Promo code (optional)" field: typed in upper case without spaces, "Apply" checks it, "Remove" empties it.
 * One message per refusal under the field; the payment stays possible without a code.
 */
@Composable
fun PromoCodeField(
    state: PromoCodeUiState,
    onInput: (String) -> Unit,
    onApply: () -> Unit,
    onRemove: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
) {
    Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(4.dp)) {
        TextNormal(
            stringResource(Res.string.promo_field_label),
            fontSize = 12.sp,
            fontWeight = FontWeight.SemiBold,
            color = AppColor.StrikeText
        )
        val applied = state.applied
        if (applied != null) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(10.dp))
                    .background(PromoSuccess.copy(alpha = 0.12f))
                    .padding(horizontal = 12.dp, vertical = 8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(Modifier.weight(1f)) {
                    TextNormal(
                        stringResource(Res.string.promo_field_applied, applied.code),
                        fontSize = 13.sp,
                        fontWeight = FontWeight.Bold,
                        color = PromoSuccess
                    )
                    TextNormal(promoBenefitLabel(applied.benefit), fontSize = 12.sp, color = AppColor.StrikeText)
                }
                TextNormal(
                    stringResource(Res.string.promo_field_remove),
                    modifier = Modifier.clickable(enabled = enabled) { onRemove() }.padding(6.dp),
                    fontSize = 13.sp,
                    color = PromoError
                )
            }
            return@Column
        }
        Row(verticalAlignment = Alignment.CenterVertically) {
            OutlinedTextField(
                modifier = Modifier.weight(1f),
                value = state.input,
                onValueChange = onInput,
                enabled = enabled && !state.checking,
                isError = state.error != null,
                singleLine = true,
                placeholder = {
                    TextNormal(
                        stringResource(Res.string.promo_field_placeholder),
                        fontSize = 13.sp,
                        color = Color(0xFF5B6678)
                    )
                },
                keyboardOptions = KeyboardOptions(
                    capitalization = KeyboardCapitalization.Characters,
                    imeAction = ImeAction.Done
                ),
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedContainerColor = Color.White,
                    unfocusedContainerColor = Color.White,
                    disabledContainerColor = Color.White,
                    focusedTextColor = Color(0xFF1A2433),
                    unfocusedTextColor = Color(0xFF1A2433),
                    disabledTextColor = Color(0xFF5B6678),
                    cursorColor = Color(0xFF1A2433),
                    focusedBorderColor = AppColor.StrikePrimary,
                    unfocusedBorderColor = Color(0xFF8A94A6),
                    errorBorderColor = PromoError,
                    errorContainerColor = Color.White,
                    errorTextColor = Color(0xFF1A2433),
                )
            )
            Spacer(Modifier.width(8.dp))
            val canApply = enabled && canApplyPromoCode(state.input, state.checking)
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(10.dp))
                    .background(if (canApply) AppColor.StrikePrimary else AppColor.StrikePrimary.copy(alpha = 0.35f))
                    .clickable(enabled = canApply) { onApply() }
                    .padding(horizontal = 14.dp, vertical = 14.dp),
                contentAlignment = Alignment.Center
            ) {
                TextNormal(
                    stringResource(if (state.checking) Res.string.promo_field_checking else Res.string.promo_field_apply),
                    fontSize = 13.sp,
                    fontWeight = FontWeight.Bold,
                    color = Color.White
                )
            }
        }
        state.error?.let { failure ->
            TextNormal("✕ " + stringResource(promoCheckFailureMessage(failure)), fontSize = 12.sp, color = PromoError)
        }
    }
}

/**
 * Whether a subscription is running for the promo field (a code is refused with a running subscription):
 * null when the status is unknown (the field is shown, the server decides).
 */
fun AccountStatus?.promoSubscriptionRunning(): Boolean? {
    if (this == null) return null
    val subscription = subscription ?: return false
    return when (subscription.publicState()) {
        null -> null
        SubscriptionPublicState.Ended -> false
        else -> true
    }
}
